import { useMemo } from "react";

import { PayoutProgram, PayoutTimeGranularity } from "../../gql-generated/types";
import { usePayoutStatsHistory } from "../../lens-insights/hooks/usePayoutStatsHistory";

import { useSelectedLens } from "./useSelectedLens";

export interface LensPlusPayoutHistory {
    lensName?: string;
    points: { month: string; amountMilliUsd: number }[];
    paidPoints: LensPlusPayoutHistory["points"];
    lastPayout: LensPlusPayoutHistory["points"][number] | null;
    loading: boolean;
    hasError: boolean;
    hasRefreshError: boolean;
    hasMore: boolean;
    loadingMore: boolean;
    hasMoreError: boolean;
    fetchMore: () => void;
    refetch: () => void;
}

export const useLensPlusPayoutHistory = (
    month: string,
    lensId?: string | null,
    allTime = false,
    skip = false,
): LensPlusPayoutHistory => {
    const { lens, unlockableId, query: selectedLens } = useSelectedLens(lensId, skip);
    const selected = new Date(`${month}-01T00:00:00Z`);
    const now = new Date();
    const monthAge =
        (now.getUTCFullYear() - selected.getUTCFullYear()) * 12 + now.getUTCMonth() - selected.getUTCMonth();
    // Include the selected trailing window when reopening an older All time drilldown.
    const history = usePayoutStatsHistory({
        payoutProgram: PayoutProgram.LensPlus,
        // Reopening the dashboard must pick up newly paid records and top-ups.
        fetchPolicy: "cache-and-network",
        countryFilters: [],
        granularity: PayoutTimeGranularity.TimeGranularityMonthly,
        limit: Math.max(24, monthAge + 12),
        unlockableId,
        skip: skip || Boolean(lensId && !unlockableId),
    });
    const { loading, error, periods, hasMore, loadingMore, hasMoreError, fetchMore } = history;
    const active = !skip && !(lensId && !unlockableId);
    const earliest = new Date(Date.UTC(selected.getUTCFullYear(), selected.getUTCMonth() - 11, 1))
        .toISOString()
        .slice(0, 7);
    const paidPoints = useMemo(
        () =>
            (active ? [...new Map(periods.map((period) => [period.period?.slice(0, 7), period])).values()] : [])
                .flatMap((period) =>
                    period.period && period.totalPayoutCount > 0
                        ? [
                              {
                                  month: period.period.slice(0, 7),
                                  amountMilliUsd: period.totalAmountMilliUsd,
                              },
                          ]
                        : [],
                )
                .sort((left, right) => left.month.localeCompare(right.month)),
        [periods, active],
    );
    return {
        lensName: lens?.name,
        points: allTime ? paidPoints : paidPoints.filter((point) => point.month >= earliest && point.month <= month),
        paidPoints,
        hasMore,
        loadingMore: loadingMore ?? false,
        hasMoreError: hasMoreError ?? false,
        hasRefreshError: Boolean(error && periods.length > 0),
        fetchMore,
        refetch: () => {
            if (lensId && !unlockableId)
                void selectedLens.refetch?.().catch(() => {
                    // Apollo exposes the failure through query state for the scoped retry UI.
                });
            else {
                history.refetch?.();
            }
        },
        lastPayout: paidPoints.at(-1) ?? null,
        loading: !skip && ((loading && periods.length === 0) || Boolean(lensId && selectedLens.loading)),
        hasError:
            !skip &&
            (Boolean(error && periods.length === 0) ||
                Boolean(lensId && (selectedLens.error ?? (!selectedLens.loading && !unlockableId)))),
    };
};
